import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient, axiosInstance } from '../services/apiClient';

interface User {
  id: string;
  email: string;
  firstname?: string | null;
  lastname?: string | null;
  role?: string;
  is_active: boolean;
  is_verified: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  inviteUser: (email: string, role: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const userData = await apiClient.get_me_auth_me_get();
      setUser(userData as any);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('access_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await axiosInstance.post('/auth/login', {
        email,
        password,
      });
      // Il refresh token arriva in un cookie HttpOnly (gestito dal browser): nel body c'è solo l'access token.
      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);
      await fetchUser();
    } catch (error) {
      throw error;
    }
  };

  const inviteUser = async (email: string, role: string) => {
    try {
      await axiosInstance.post('/auth/invite-user', { email, role });
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    // Pulisce subito lo stato locale: l'utente esce anche se il server non risponde.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token'); // residuo di versioni precedenti
    setUser(null);
    try {
      // Revoca lato server la famiglia di refresh token e cancella il cookie HttpOnly.
      await apiClient.logout_auth_logout_post();
    } catch (error) {
      // Ignora errori nel logout
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        inviteUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
